"use client";

/**
 * Shared module-level definitions for the Fill & Sign tool: field/mode/font
 * types, the toolbar table, glyph rendering, ink/size tables, id + zoom
 * helpers. Relocated verbatim from FillAndSignTool.tsx (bodies unchanged).
 */
import React from "react";

/* ─── Types ─── */
export type FieldType = "text" | "date" | "signature" | "check" | "cross" | "dot" | "whiteout";
export type InteractionMode =
  | "select"
  | "add-text"
  | "add-date"
  | "add-signature"
  | "add-check"
  | "add-cross"
  | "add-dot"
  | "add-whiteout";
export type FontFamily = "sans" | "serif" | "mono";
export type FontSize = "small" | "medium" | "large";

/* Marks (✓ ✗ •) and signatures are "glyph-like": no font/size, aspect-locked resize */
export const isMark = (t: FieldType): boolean => t === "check" || t === "cross" || t === "dot";
export const isGlyphLike = (t: FieldType): boolean => t === "signature" || isMark(t);
/* Fields whose property bar is just a label + delete (no font/size) */
export const isMinimalBar = (t: FieldType): boolean => isGlyphLike(t) || t === "whiteout";
export const MODE_TO_MARK: Partial<Record<InteractionMode, FieldType>> = {
  "add-check": "check",
  "add-cross": "cross",
  "add-dot": "dot",
};
export const GLYPH_LABEL: Partial<Record<FieldType, string>> = {
  signature: "Signature",
  check: "Checkmark",
  cross: "Cross",
  dot: "Dot",
  whiteout: "Whiteout",
};

/* Toolbar definition — single source for desktop bar, mobile bar, and keyboard */
export interface ToolDef { mode: InteractionMode; label: string; key: string; icon: string; iconFilled?: boolean; }
export const TOOLS: ToolDef[] = [
  { mode: "select", label: "Select", key: "V", icon: "M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834.166l-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243l-1.59-1.59" },
  { mode: "add-text", label: "Text", key: "T", icon: "M2.243 4.493v7.5m0 0v7.502m0-7.501h10.5m0-7.5v7.5m0 0v7.501" },
  { mode: "add-date", label: "Date", key: "D", icon: "M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" },
  { mode: "add-signature", label: "Sign", key: "S", icon: "M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" },
  { mode: "add-check", label: "Check", key: "C", icon: "M4.5 12.75l6 6 9-13.5" },
  { mode: "add-cross", label: "Cross", key: "X", icon: "M6 18L18 6M6 6l12 12" },
  { mode: "add-dot", label: "Dot", key: "O", icon: "M12 9.75a2.25 2.25 0 100 4.5 2.25 2.25 0 000-4.5z", iconFilled: true },
  { mode: "add-whiteout", label: "Whiteout", key: "W", icon: "M3.75 6.75A2.25 2.25 0 016 4.5h12a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0118 19.5H6a2.25 2.25 0 01-2.25-2.25V6.75z" },
];

/* Selection / type chrome for a placed field's overlay box */
export function fieldChrome(type: FieldType, isSelected: boolean, isEditing: boolean): string {
  if (type === "whiteout") return isSelected ? "border-blue-500 bg-white ring-2 ring-blue-500/50" : "border-slate-300 bg-white";
  if (isSelected) return "border-blue-500 bg-blue-50/30 ring-2 ring-blue-500/40";
  if (type === "signature") return "border-[var(--color-gold)] bg-[var(--color-gold-muted)]/20";
  if (type === "date") return "border-violet-400 bg-violet-50/40";
  if (isMark(type)) return "border-sky-400 bg-sky-50/40";
  if (isEditing) return "border-blue-400 border-dashed bg-white/60";
  return "border-emerald-400 bg-emerald-50/40";
}

/* Glyph renderer — used for placed marks and the signature/mark property-bar icon */
export function GlyphIcon({ type, className, style }: { type: FieldType; className?: string; style?: React.CSSProperties }) {
  if (type === "dot") {
    return (
      <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <circle cx="12" cy="12" r="6" />
      </svg>
    );
  }
  if (type === "whiteout") {
    return (
      <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
        <rect x="4" y="5" width="16" height="14" rx="1.5" />
      </svg>
    );
  }
  const d =
    type === "check"
      ? "M4.5 12.75l6 6 9-13.5"
      : type === "cross"
      ? "M6 18L18 6M6 6l12 12"
      : "M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z";
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={type === "signature" ? 1.5 : 3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}

export interface PlacedField {
  id: string;
  type: FieldType;
  pageIndex: number;
  xNorm: number;
  yNorm: number;
  wNorm: number;
  hNorm: number;
  value: string;
  signatureDataUrl?: string;
  font: FontFamily;
  size: FontSize;
  customPx?: number; // Custom font size in px (overrides preset)
  color?: string; // ink color (hex) for text/date/marks; ignored for signature/whiteout
}

export const DEFAULT_INK = "#111827";
export const INK_PRESETS = ["#111827", "#1d4ed8", "#dc2626", "#16a34a"]; // black, blue, red, green

export const FONT_CSS: Record<FontFamily, string> = {
  sans: "Helvetica, Arial, sans-serif",
  serif: "Times New Roman, Georgia, serif",
  mono: "Courier New, Courier, monospace",
};

export const SIZE_PX: Record<FontSize, number> = { small: 10, medium: 14, large: 18 };
export const SIZE_LABEL: Record<FontSize, string> = { small: "S", medium: "M", large: "L" };

/* Height (normalized) needed per font size — ensures text is never clipped.
   Based on: fontSize + 8px padding, converted to fraction of ~1000px rendered page. */
export const SIZE_H_NORM: Record<FontSize, number> = { small: 0.025, medium: 0.03, large: 0.038 };

/* Convert custom px to nearest preset (for height calc) */
export function pxToPreset(px: number): FontSize {
  if (px <= 11) return "small";
  if (px <= 16) return "medium";
  return "large";
}

/* Normalized height for arbitrary px */
export function customPxHNorm(px: number): number {
  return Math.max(0.02, Math.min(0.08, (px + 8) / 1000));
}

export function makeId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export const HISTORY_LIMIT = 50;
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 3;
export const ZOOM_STEP = 0.25;
export const clampZoom = (z: number) => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, +z.toFixed(2)));
